import axios from "axios";
import { getClientConfig } from "./clientConfigCache";

// Funzione per ottenere il CSRF token dai cookie
const getCSRFToken = () => {
  const match = document.cookie.match(new RegExp('(^| )XSRF-TOKEN=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

// Funzione per refreshare il token (usa cache condivisa per /client-config)
const refreshToken = async () => {
  try {
    console.log("[API Service] Tentativo di refresh del token...");
    const config = await getClientConfig();
    if (!config) throw new Error('Impossibile ottenere le credenziali');
    const email = config.REACT_APP_API_EMAIL || window.REACT_APP_API_EMAIL || process.env.REACT_APP_API_EMAIL;
    const password = config.REACT_APP_API_PASSWORD || process.env.REACT_APP_API_PASSWORD || 'ABQJthtHghT784N';
    
    if (!email || !password) {
      throw new Error('Credenziali non disponibili per il refresh');
    }

    // Prima ottieni un nuovo CSRF token
    await axios.get('/sanctum/csrf-cookie', { withCredentials: true });
    
    // Poi effettua il login per ottenere un nuovo token
    const baseUrl = window.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || '/api';
    const loginResponse = await axios.post(`${baseUrl}/users/login`, {
      email,
      password
    }, {
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      withCredentials: true
    });

    if (loginResponse.data && loginResponse.data.status === "success" && loginResponse.data.data?.token) {
      const newToken = loginResponse.data.data.token;
      localStorage.setItem("apiToken", newToken);
      console.log("[API Service] Token refreshato con successo");
      return newToken;
    } else {
      throw new Error('Risposta di login non valida');
    }
  } catch (error) {
    console.error("[API Service] Errore durante il refresh del token:", error);
    // Rimuovi il token non valido
    localStorage.removeItem("apiToken");
    // Trigger per il logout o redirect alla pagina di login
    window.dispatchEvent(new CustomEvent('tokenExpired'));
    throw error;
  }
};

const apiService = async (url, method = "GET", data = null, options = {}, retryCount = 0) => {
  try {
    const baseUrl = window.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || '/api';
    const fullUrl = url.startsWith("/sanctum") ? url : `${baseUrl}${url}`;

    const token = localStorage.getItem("apiToken");
    const csrfToken = getCSRFToken();
    
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-Requested-With": "XMLHttpRequest", // IMPORTANTE per Laravel Sanctum
      ...options.headers // Merge custom headers
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    // Aggiungi il CSRF token se presente
    if (csrfToken) {
      headers["X-XSRF-TOKEN"] = csrfToken;
    }

    // console.log("[API Service] Inizio richiesta:", { url, method, data });
    // console.log("[API Service] Headers:", headers);
    
    // Log specifico per chiamate PUT
    if (method === 'PUT') {
      console.log("🔄 [API Service] CHIAMATA PUT DETECTATA");
      console.log("📡 URL:", fullUrl);
      console.log("📦 Data:", data);
      console.log("📏 Data size:", JSON.stringify(data || {}).length, "bytes");
    }

    const response = await axios({
      url: fullUrl,
      method,
      data,
      headers,
      withCredentials: true,
      ...options // Pass other options like responseType
    });

    // console.log("[API Service] Risposta ricevuta:", response);

    return response.data;
  } catch (error) {
    console.error("Errore nella richiesta API:", error.response || error.message);

    // Messaggio server da response.data.error (es. PUT/POST) — usato dai toaster/UI
    const respData = error.response?.data;
    if (respData != null && respData.error != null && respData.error !== "") {
      const ev = respData.error;
      error.apiErrorMessage =
        typeof ev === "string"
          ? ev
          : typeof ev === "object"
            ? JSON.stringify(ev)
            : String(ev);
    }

    // Gestione automatica del refresh del token per errori 401 (Unauthorized)
    if (error.response && error.response.status === 401 && retryCount === 0) {
      console.log("[API Service] Token scaduto, tentativo di refresh...");
      try {
        await refreshToken();
        // Riprova la richiesta con il nuovo token
        return await apiService(url, method, data, options, retryCount + 1);
      } catch (refreshError) {
        console.error("[API Service] Impossibile refreshare il token:", refreshError);
        throw error; // Rilancia l'errore originale
      }
    }
    
    // Log dettagliato per errori CSRF
    if (error.response && error.response.status === 419) {
      console.error("Errore CSRF token mismatch. Potrebbe essere necessario rinnovare il CSRF cookie.");
    }
    
    throw error;
  }
};

export const getStorageItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Errore nel recupero dell'elemento ${key} da localStorage:`, error);
    return null;
  }
};

export default apiService;